<template>
  <v-autocomplete
    :key="`location-field-${mainSearchBarStore.locationKey}`"
    no-filter
    :density="displayCompact ? 'compact' : 'default'"
    v-model="searchLocation"
    v-bind="attrs"
    :items="computedItems"
    :loading="addressAutocompleteStore.loadingLocation"
    :bg-color="smAndUp ? 'transparent' : 'white'"
    :class="smAndUp ? 'w-50' : 'bg-white rounded-pill'"
    :label="$t('heroSection.searchPlaceholder2')"
    :placeholder="$t('heroSection.searchPlaceholder2')"
    :no-data-text="$t('heroSection.noAddressFound')"
    :hide-no-data="!menu"
    class="location-field"
    variant="solo"
    :prepend-inner-icon="displayIcon ? 'mdi-map-marker-radius-outline' : ''"
    flat
    hide-details
    clearable
    single-line
    item-title="label"
    item-value="value"
    return-object
    :auto-select-first="true"
    autocomplete="none"
    @update:search="handleUpdateLocation"
    @update:model-value="
      $event => handleUpdateLocation($event as unknown as LocationItemWithId)
    "
  >
    <template #append-inner>
      <v-btn
        v-if="
          geolocationStore.loading || addressAutocompleteStore.loadingLocation
        "
        icon
        size="small"
        variant="text"
        :loading="
          geolocationStore.loading ||
          (addressAutocompleteStore.loadingLocation as unknown as boolean)
        "
      ></v-btn>
    </template>
    <template #append>
      <v-tooltip text="Utiliser ma localisation" location="bottom">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            class="z-100 mr-2"
            size="small"
            icon="mdi-crosshairs"
            variant="text"
            @click.prevent.stop="() => myLocation()"
          ></v-btn>
        </template>
      </v-tooltip>
    </template>
    <!-- <v-menu
      v-model="menu"
      activator="parent"
      max-height="300px"
      @update:model-value="closeMenuIfEmpty"
    >
      <v-list
        v-if="0 < addressAutocompleteStore.autocompleteLocationList.length"
        :items="
          addressAutocompleteStore.autocompleteLocationList as unknown as any[]
        "
        item-title="label"
        item-value="item"
        return-object
        @click:select="
          $event => handleUpdateLocation($event.id as LocationItemWithId)
        "
      >
      </v-list>
    </v-menu> -->
  </v-autocomplete>
</template>

<script lang="ts" setup>
import {
  LocationItem,
  LocationItemWithId,
} from '@/store/address-autocomplete/LocationItemInterface'
import { useAutocompleteLocationStore } from '@/store/address-autocomplete/autocompleteAddressStore'
import { useGeolocationStore } from '@/store/geolocation/geolocationStore'
import { useGoogleMapsStore } from '@/store/google-maps/googleMapsStore'
import { useMainSearchBarStore } from '@/store/search-page/mainSearchBarStore'
import { ref, watch, nextTick, useAttrs, computed, onMounted } from 'vue'
import { useDisplay } from 'vuetify'
import { useRoute } from 'vue-router'

const attrs = useAttrs()
const { smAndUp } = useDisplay()
const mainSearchBarStore = useMainSearchBarStore()
const geolocationStore = useGeolocationStore()
const addressAutocompleteStore = useAutocompleteLocationStore()
const menu = ref(false)
const route = useRoute()
const searchLocation = ref<string>(null)

const locationKey = ref<number>(1)

watch(
  () => mainSearchBarStore.locationKey,
  async () => {
    await nextTick()
  },
)

const renderingLocationField = async () => {
  searchLocation.value = mainSearchBarStore.searchLocation
  mainSearchBarStore.locationKey++
  await nextTick()
}

defineProps({
  displayIcon: {
    type: Boolean,
    default: true,
  },
  displayCompact: {
    type: Boolean,
    default: false,
  },
})

watch(
  () => searchLocation.value,
  v => {
    if (!v) {
      mainSearchBarStore.searchLocation = null
    }
  },
)

watch(
  () => mainSearchBarStore.searchLocation,
  (newval, oldval) => {
    nextTick(() => {
      closeMenuIfEmpty()
      if (
        mainSearchBarStore.searchLocation &&
        0 < addressAutocompleteStore.autocompleteLocationList.length
      ) {
        menu.value = true
      }
      if (addressAutocompleteStore.autocompleteLocationList.length === 0) {
        menu.value = false
      }

      mainSearchBarStore.alreadySearchWithSelectedCity = false
    })
  },
)

watch(
  () => addressAutocompleteStore.autocompleteLocationList,
  newList => {
    if (newList.length > 0) {
      menu.value = true
    }
  },
)

const closeMenuIfEmpty = () => {
  if (
    0 === addressAutocompleteStore.autocompleteLocationList.length ||
    !mainSearchBarStore.searchLocation
  ) {
    menu.value = false
  }
}

const IS_USING_GOOGLE_MAPS_API = true

const myLocation = async () => {
  geolocationStore.loading = true
  addressAutocompleteStore.selectedLocation = null
  await geolocationStore.askGeolocation()
  renderingLocationField()
}

/**
 * Function that fetches the autocomplete list on type with debounce or handles the item selection in the menu
 * @param locationItemOrText
 */
async function handleUpdateLocation(
  locationItemOrText: LocationItemWithId | string,
) {
  if (
    locationItemOrText &&
    typeof locationItemOrText === 'object' &&
    Object.prototype.hasOwnProperty.call(locationItemOrText, 'value') &&
    locationItemOrText.value === '__MY_POSITION__'
  ) {
    await myLocation()
    return
  }

  const googleMapsStore = useGoogleMapsStore()
  if (typeof locationItemOrText === 'string' && locationItemOrText.length > 0) {
    mainSearchBarStore.searchLocation = locationItemOrText
    addressAutocompleteStore.selectedLocation = null // reset selected location item on type
  }
  if (locationItemOrText) {
    menu.value = true
  } else {
    menu.value = false
  }
  if (!locationItemOrText) {
    addressAutocompleteStore.autocompleteLocationList = []
  } else if (
    typeof locationItemOrText === 'object' &&
    Object.prototype.hasOwnProperty.call(locationItemOrText, 'value')
  ) {
    // case item in the menu is clicked
    locationItemOrText = locationItemOrText as LocationItemWithId
    // avoir typescript error

    if (IS_USING_GOOGLE_MAPS_API) {
      ;(locationItemOrText as any).value =
        await googleMapsStore.getFormatedAddressFromPlace(
          locationItemOrText.value,
        )
    }

    const locationItem = locationItemOrText as unknown as LocationItem

    geolocationStore.updateStoredLocation({
      ...locationItem.value,
      coords: {
        latitude: Number(locationItem.value.latitude),
        longitude: Number(locationItem.value.longitude),
      },
    })

    addressAutocompleteStore.selectedLocation = locationItem
    mainSearchBarStore.searchLocation = locationItem.label || null
    return
  } else if (typeof locationItemOrText === 'string') {
    // Case user is typing
    // addressAutocompleteStore.autocompleteLocationRequest(locationItemOrText) // old GeoCode
    await addressAutocompleteStore.autocompleteRequest(locationItemOrText)
  }
}

const computedItems = computed(() => {
  const locationLists = addressAutocompleteStore.autocompleteLocationList as any

  return [
    { label: 'Utiliser ma localisation', value: '__MY_POSITION__' },
    ...locationLists,
  ]
})

onMounted(() => {
  searchLocation.value = mainSearchBarStore.searchLocation
})

defineExpose({
  renderingLocationField,
})
</script>

<style scoped>
.location-field:deep() .v-input__append {
  margin-inline-start: 0px;
}

.location-field >>> input {
  opacity: 1 !important;
  color: black;
}

/* .location-field input::placeholder {
  opacity: 1 !important;
  color: black;
} */
</style>
